import { useState, useEffect, useCallback } from 'react';
import { CptView } from '../../../../../../../services/ContractService/types/contractTypes';
import { createDefaultCpt } from '../../../../../../../services/ContractService/createDefaultCpt';

export interface CptFormData {
    code: string;
    name: string;
    category: string;
    apcCode: string;
    baseRate: number;
    grouperPointer: string;
    multiProcedureDiscount: boolean;
    relativeWeight: number | null;
}

export interface CptFormErrors {
    code?: string;
    name?: string;
    category?: string;
    apcCode?: string;
    baseRate?: string;
    grouperPointer?: string;
    relativeWeight?: string;
}

interface UseCptFormProps {
    editingCpt: CptView | null;
    feeScheduleId: string;
    open: boolean;
}

interface UseCptFormResult {
    formData: CptFormData;
    formErrors: CptFormErrors;
    handleFormChange: (field: keyof CptFormData, value: any) => void;
    validateForm: () => boolean;
    resetErrors: () => void;
}

export const useCptForm = ({
    editingCpt,
    feeScheduleId,
    open
}: UseCptFormProps): UseCptFormResult => {
    const [formData, setFormData] = useState<CptFormData>({
        code: '',
        name: '',
        category: '',
        apcCode: '',
        baseRate: 0,
        grouperPointer: '',
        multiProcedureDiscount: false,
        relativeWeight: null,
    });
    const [formErrors, setFormErrors] = useState<CptFormErrors>({});

    // Initialize form when dialog opens or editing CPT changes
    useEffect(() => {
        if (open) {
            if (editingCpt) {
                setFormData({
                    code: editingCpt.code,
                    name: editingCpt.name,
                    category: editingCpt.category,
                    apcCode: editingCpt.apcCode,
                    baseRate: editingCpt.baseRate,
                    grouperPointer: editingCpt.grouperPointer,
                    multiProcedureDiscount: editingCpt.multiProcedureDiscount,
                    relativeWeight: editingCpt.relativeWeight ?? null,
                });
            } else {
                const defaultCpt = createDefaultCpt(feeScheduleId);
                setFormData({
                    code: defaultCpt.code,
                    name: defaultCpt.name,
                    category: defaultCpt.category,
                    apcCode: defaultCpt.apcCode,
                    baseRate: defaultCpt.baseRate,
                    grouperPointer: defaultCpt.grouperPointer,
                    multiProcedureDiscount: defaultCpt.multiProcedureDiscount,
                    relativeWeight: defaultCpt.relativeWeight ?? null,
                });
            }
            setFormErrors({});
        }
    }, [open, editingCpt, feeScheduleId]);

    const handleFormChange = useCallback((field: keyof CptFormData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));

        // Clear error when user starts typing
        setFormErrors(prev => ({
            ...prev,
            [field]: undefined,
        }));
    }, []);

    const validateForm = useCallback((): boolean => {
        const errors: CptFormErrors = {};

        if (!formData.code.trim()) {
            errors.code = 'Code is required';
        }

        if (!formData.name.trim()) {
            errors.name = 'Name is required';
        }

        if (formData.baseRate < 0) {
            errors.baseRate = 'Base rate cannot be negative';
        }

        if (formData.relativeWeight !== null && formData.relativeWeight < 0) {
            errors.relativeWeight = 'Relative weight cannot be negative';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData]);

    const resetErrors = useCallback(() => {
        setFormErrors({});
    }, []);

    return {
        formData,
        formErrors,
        handleFormChange,
        validateForm,
        resetErrors
    };
};
